// =============================================================================
// ZONE EDITOR 3D - MAIN COMPONENT
// Espositore con piano inclinato 30° + LIDAR + zone + scansione real-time
// =============================================================================

import { useState, useCallback, useEffect } from 'react'
import * as THREE from 'three'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { Play, Square } from 'lucide-react'
import { DisplayCase } from './DisplayCase'
import { LidarMarker } from './LidarMarker'
import { LidarPoints3D } from './LidarPoints3D'
import { ZonePolygon } from './ZonePolygon'
import { mmToSceneScale, degreesToRadians, FIXED_SURFACE_TILT_DEG } from './utils'
import { wsService } from '../../services/websocket'
import type { ZoneEditor3DProps } from './types'

export function ZoneEditor3D({
  zones,
  workspace,
  selectedZone,
  onSelectZone,
  deviceId,
  isDeviceOnline = false,
}: ZoneEditor3DProps) {
  const [isStreaming, setIsStreaming] = useState(false)
  const [surfaceTiltDeg, setSurfaceTiltDeg] = useState(FIXED_SURFACE_TILT_DEG)

  const w = mmToSceneScale(workspace.width)
  const h = mmToSceneScale(workspace.height)
  const maxDim = Math.max(w, h)

  // Surface tilt (dynamic)
  const surfaceTiltRad = degreesToRadians(surfaceTiltDeg)

  // LIDAR height from ground (slider controls this, table adapts)
  // lidarGroundHeight = tableHeight + lidar_y * sin(tilt) (in mm)
  const lidarRiseFromTilt = workspace.lidar_y * Math.sin(surfaceTiltRad)
  const defaultLidarHeight = Math.round(maxDim * 0.4 * 1000 + lidarRiseFromTilt)
  const [lidarHeightMm, setLidarHeightMm] = useState(defaultLidarHeight)
  // Derive table front edge height from LIDAR height
  const tableHeight = Math.max(0.01, (lidarHeightMm - lidarRiseFromTilt) / 1000)

  // LIDAR position on tilted surface (local coords, same as flat)
  const lidarSx = mmToSceneScale(workspace.lidar_x)
  const lidarSz = -mmToSceneScale(workspace.lidar_y)

  // Sensor elevation from surface (dynamic, in mm)
  const minDim = Math.min(w, h)
  const defaultSensorMm = Math.round(Math.max(15, minDim * 0.02 * 2.5 * 1.7 * 1000))
  const [sensorElevationMm, setSensorElevationMm] = useState(defaultSensorMm)
  const sensorY = sensorElevationMm / 1000 // scene units

  // Scan tilt (LIDAR's own tilt, separate from surface tilt)
  const scanTiltRad = degreesToRadians(Math.abs(workspace.lidar_tilt))
  const tiltSign = workspace.lidar_tilt < 0 ? -1 : 1

  // Scan plane visual radius
  const scanPlaneRadius = maxDim * 1.2

  // Camera target: center of tilted surface
  const centerX = w / 2
  const centerY = tableHeight + (h / 2) * Math.sin(surfaceTiltRad)
  const centerZ = -(h / 2) * Math.cos(surfaceTiltRad)
  const camDist = maxDim * 2.0
  const cameraPosition: [number, number, number] = [
    centerX + camDist * 0.5,
    centerY + camDist * 0.8,
    centerZ + camDist * 0.6
  ]

  // Start/stop LIDAR streaming
  const toggleStreaming = useCallback(() => {
    if (!deviceId) return
    if (isStreaming) {
      wsService.stopLidarStream(deviceId)
      setIsStreaming(false)
    } else {
      wsService.startLidarStream(deviceId, 0.2)
      setIsStreaming(true)
    }
  }, [deviceId, isStreaming])

  useEffect(() => {
    if (!isDeviceOnline && isStreaming && deviceId) {
      wsService.stopLidarStream(deviceId)
      setIsStreaming(false)
    }
  }, [isDeviceOnline, isStreaming, deviceId])

  useEffect(() => {
    return () => {
      if (isStreaming && deviceId) {
        wsService.stopLidarStream(deviceId)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative w-full h-full bg-gray-900 rounded-lg overflow-hidden">
      {/* LIDAR stream control overlay */}
      {deviceId && isDeviceOnline && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
          <button
            onClick={toggleStreaming}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              isStreaming
                ? 'bg-red-500/90 hover:bg-red-600 text-white'
                : 'bg-emerald-500/90 hover:bg-emerald-600 text-white'
            }`}
          >
            {isStreaming ? (
              <>
                <Square size={12} />
                Stop LIDAR
              </>
            ) : (
              <>
                <Play size={12} />
                Start LIDAR
              </>
            )}
          </button>
          {isStreaming && (
            <span className="flex items-center gap-1.5 text-xs text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Live
            </span>
          )}
        </div>
      )}

      {/* Surface controls overlay */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5 bg-black/50 rounded-md px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-300 w-16">Inclinazione</span>
          <input
            type="range"
            min={0}
            max={60}
            value={surfaceTiltDeg}
            onChange={(e) => setSurfaceTiltDeg(Number(e.target.value))}
            className="w-20 h-1 accent-teal-500"
          />
          <span className="text-xs text-white font-mono w-8">{surfaceTiltDeg}°</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-300 w-16">Alt. Piano</span>
          <input
            type="range"
            min={50}
            max={800}
            value={lidarHeightMm}
            onChange={(e) => setLidarHeightMm(Number(e.target.value))}
            className="w-20 h-1 accent-teal-500"
          />
          <span className="text-xs text-white font-mono w-12">{lidarHeightMm}mm</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-300 w-16">Alt. LIDAR</span>
          <input
            type="range"
            min={5}
            max={150}
            value={sensorElevationMm}
            onChange={(e) => setSensorElevationMm(Number(e.target.value))}
            className="w-20 h-1 accent-teal-500"
          />
          <span className="text-xs text-white font-mono w-12">{sensorElevationMm}mm</span>
        </div>
      </div>

      <Canvas
        camera={{ position: cameraPosition, fov: 45, near: 0.001, far: 500 }}
        shadows
        onPointerMissed={() => onSelectZone(null)}
      >
        {/* Lighting */}
        <ambientLight intensity={0.6} />
        <directionalLight
          position={[centerX + maxDim * 2, centerY + maxDim * 3, centerZ + maxDim]}
          intensity={1.0}
          castShadow
        />
        <directionalLight
          position={[centerX - maxDim, centerY + maxDim * 2, centerZ - maxDim]}
          intensity={0.4}
        />
        <directionalLight
          position={[centerX, centerY + maxDim, centerZ + maxDim * 2]}
          intensity={0.5}
        />

        <color attach="background" args={['#0f172a']} />

        {/* Floor */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[centerX, -0.001, centerZ]} receiveShadow>
          <planeGeometry args={[maxDim * 5, maxDim * 5]} />
          <meshStandardMaterial color="#0a0f1a" roughness={1} />
        </mesh>

        {/* ============= ESPOSITORE (struttura con piano inclinato) ============= */}
        <DisplayCase workspace={workspace} tableHeight={tableHeight} surfaceTiltDeg={surfaceTiltDeg} />

        {/* ============= TILTED SURFACE GROUP ============= */}
        {/* Everything on the surface shares the same tilt transform */}
        <group position={[0, tableHeight, 0]} rotation={[surfaceTiltRad, 0, 0]}>

          {/* Zone */}
          {zones.map((zone, index) => (
            <ZonePolygon
              key={zone.id}
              zone={zone}
              isSelected={selectedZone?.id === zone.id}
              onClick={() => onSelectZone(zone)}
              colorIndex={index}
            />
          ))}

          {/* LIDAR points real-time */}
          {deviceId && isStreaming && (
            <LidarPoints3D
              deviceId={deviceId}
              lidarX={workspace.lidar_x}
              lidarY={workspace.lidar_y}
              lidarRotation={workspace.lidar_rotation}
              workspaceWidth={workspace.width}
              workspaceHeight={workspace.height}
            />
          )}

          {/* LIDAR sensor */}
          <group position={[lidarSx, 0, lidarSz]}>
            <LidarMarker workspace={workspace} sensorElevation={sensorY} />

            {/* Scan plane (tilted by LIDAR's own tilt, relative to surface) */}
            <group position={[0, sensorY, 0]} rotation={[-tiltSign * scanTiltRad, 0, 0]}>
              <mesh rotation={[-Math.PI / 2, 0, 0]}>
                <circleGeometry args={[scanPlaneRadius, 64]} />
                <meshBasicMaterial
                  color="#14b8a6"
                  transparent
                  opacity={0.06}
                  side={THREE.DoubleSide}
                  depthWrite={false}
                />
              </mesh>
              <mesh rotation={[-Math.PI / 2, 0, 0]}>
                <ringGeometry args={[scanPlaneRadius * 0.97, scanPlaneRadius, 64]} />
                <meshBasicMaterial
                  color="#14b8a6"
                  transparent
                  opacity={0.25}
                  side={THREE.DoubleSide}
                />
              </mesh>
              <lineSegments>
                <bufferGeometry>
                  <bufferAttribute
                    attach="attributes-position"
                    array={new Float32Array([
                      -scanPlaneRadius, 0, 0, scanPlaneRadius, 0, 0,
                      0, 0, -scanPlaneRadius, 0, 0, scanPlaneRadius,
                    ])}
                    count={4}
                    itemSize={3}
                  />
                </bufferGeometry>
                <lineBasicMaterial color="#14b8a6" transparent opacity={0.15} />
              </lineSegments>
            </group>

            {/* Vertical reference line when tilt != 0 */}
            {workspace.lidar_tilt !== 0 && (
              <lineSegments>
                <bufferGeometry>
                  <bufferAttribute
                    attach="attributes-position"
                    array={new Float32Array([
                      0, sensorY - scanPlaneRadius * 0.3, 0, 0, sensorY + scanPlaneRadius * 0.3, 0,
                    ])}
                    count={2}
                    itemSize={3}
                  />
                </bufferGeometry>
                <lineBasicMaterial color="#ff6644" transparent opacity={0.4} />
              </lineSegments>
            )}
          </group>
        </group>

        {/* Camera controls */}
        <OrbitControls
          target={[centerX, centerY, centerZ]}
          enableDamping
          dampingFactor={0.1}
          maxPolarAngle={Math.PI * 0.85}
          minDistance={maxDim * 0.3}
          maxDistance={maxDim * 6}
        />
      </Canvas>
    </div>
  )
}

export default ZoneEditor3D
